import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDischargeSectionTemplates, type DischargeSectionTemplateRow } from "@/lib/queries";
import {
  useCreateDischargeSectionTemplate, useUpdateDischargeSectionTemplate, useDeleteDischargeSectionTemplate,
} from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Plus, Pencil, Trash } from "lucide-react";
import { DISCHARGE_TEMPLATE_SECTIONS, type DischargeTemplateSection } from "@emr/shared/constants";
import { toast } from "sonner";

interface DraftTemplate {
  id: string | null; // null = creating
  section: DischargeTemplateSection;
  title: string;
  content: string;
}

export default function DischargeTemplatesPage() {
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("dischargesummaries.create");
  const canEdit = isAdmin || permissions.has("dischargesummaries.edit");
  const canDelete = isAdmin || permissions.has("dischargesummaries.delete");

  const { data, isLoading } = useDischargeSectionTemplates();
  const createMut = useCreateDischargeSectionTemplate();
  const deleteMut = useDeleteDischargeSectionTemplate();

  const [editing, setEditing] = useState<DraftTemplate | null>(null);
  // The update hook is bound to the currently-edited template's id (empty
  // string when creating — the hook is unused in that branch).
  const updateMut = useUpdateDischargeSectionTemplate(editing?.id ?? "");

  const rows = data?.rows ?? [];

  const startCreate = (section: DischargeTemplateSection) => {
    setEditing({ id: null, section, title: "", content: "" });
  };

  const startEdit = (t: DischargeSectionTemplateRow) => {
    setEditing({ id: t.id, section: t.section, title: t.title, content: t.content ?? "" });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    const body = { section: editing.section, title: editing.title.trim(), content: editing.content || null };
    try {
      if (editing.id) {
        await updateMut.mutateAsync(body);
      } else {
        await createMut.mutateAsync(body);
      }
      setEditing(null);
    } catch { /* mutation toast handles */ }
  };

  const del = (t: DischargeSectionTemplateRow) => {
    // eslint-disable-next-line no-alert -- lightweight inline confirm
    if (window.confirm(`Delete template "${t.title}"?`)) {
      deleteMut.mutate(t.id);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/discharge-summaries")}><ArrowLeft size={18} /></Button>
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Discharge Templates</h1>
        </div>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle className="font-serif text-lg">{editing.id ? "Edit" : "New"} {editing.section} template</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Section</Label>
                <Input value={editing.section} disabled />
              </div>
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Standard neuro exam" />
              </div>
            </div>
            <div>
              <Label>Content</Label>
              <Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={6} placeholder="Inserted into the section when this template is picked." />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={createMut.isPending || updateMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="History">
        <TabsList>
          {DISCHARGE_TEMPLATE_SECTIONS.map((s) => (
            <TabsTrigger key={s} value={s}>{s} ({rows.filter((r) => r.section === s).length})</TabsTrigger>
          ))}
        </TabsList>
        {DISCHARGE_TEMPLATE_SECTIONS.map((s) => (
          <TabsContent key={s} value={s}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-lg">{s} templates</CardTitle>
                {canCreate && (
                  <Button size="sm" variant="outline" onClick={() => startCreate(s)}><Plus size={14} className="mr-1" /> New</Button>
                )}
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-20" />
                ) : rows.filter((r) => r.section === s).length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted">No {s} templates yet.</p>
                ) : (
                  <div className="space-y-2">
                    {rows.filter((r) => r.section === s).map((t) => (
                      <div key={t.id} className="flex items-start justify-between gap-3 rounded-md border border-cream-200 p-3 dark:border-dark-700">
                        <div className="min-w-0">
                          <p className="font-medium">{t.title}</p>
                          <p className="visible-scrollbar mt-1 max-h-16 overflow-auto whitespace-pre-wrap text-xs text-muted">{t.content ?? "—"}</p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          {canEdit && (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(t)}><Pencil size={14} /></Button>
                          )}
                          {canDelete && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(t)}><Trash size={14} /></Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
